import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
	getDocumentDetails,
	unmountDocumentProperties,
	updateDocumentProperties,
	setFavoriteDocumentProperties,
	deleteVersion,
	deleteMajorVersion,
	deleteDocuments,
	restoreDocuments,
	setLockStatus,
	getSignedUrlForDocumentAndDownload,
	updateVersionComment,
	updateMajorVersionComment,
	openDocumentInLauncher,
	convertDocumentToPDF,
	setDocumentLifecycleStatus,
	renameDocumentFromDocumentProperties,
	promoteDocumentToNextMajorVersion,
} from "../actions/documentActions";
import {
	getDisabledStateFromLifecycleStatuses,
	NORMAL_LIFECYCLE_STATUS,
	TRASH_LIFECYCLE_STATUS,
	ARCHIVE_LIFECYCLE_STATUS,
} from "../utilities/lifecycleStatus";
import { selectArchiveMatterContentsAccess } from "../utilities/archiveAccess";
import { requestOCR } from "../actions/ocrJobProgressActions";

import { openGlobalDialog } from "../actions/globalEditDialogActions";
import { uploadNewVersionWithValidation } from "../actions/uploadNewVersionActions";
import UploadVersionSplitButton from "./UploadVersionSplitButton";
import { resetTableColumnWidths } from "../actions/tableSortingActions";
import { ButtonWithIcon } from "./ButtonWithIcon";
import { DropDownButtonWithRouter } from "./DropDownButton";
import { BreadcrumbsWithMatterName } from "./Breadcrumbs";
import { LabeledTextInput } from "./TextInput";
import { LabeledTextArea } from "./TextArea";
import { LabeledDropDownSelect } from "./DropDownSelect";
import FavoriteStar from "./FavoriteStar";
import { SimpleSwitch } from "./AsyncSwitch";
import TwoColumnWrapper from "./TwoColumnWrapper";
import {
	longDateFormat,
	getCheckoutMenuItems,
	splitFilename,
	convertToPDFAllowed,
	ocrOnDemandAllowed,
	isFileTypeCompatibleWithLitera,
	isFileTypeCompatibleWithInline,
	ocrIsDisabledForDocument,
} from "../utilities/utilities";
import { addDocumentToCompare } from "../actions/documentCompareActions";
import { TrashOrArchiveBinMessageForDocumentOrNote } from "./Utils/UnderSubHeaderWarningMessages";
import DocumentVersions, {
	MANUAL_TABLE_ID as VERSIONS_MANUAL_TABLE_ID,
	AUTO_TABLE_ID as VERSIONS_AUTO_TABLE_ID,
} from "./DocumentVersions";
import DocumentSlidingPane from "./DocumentPreviewRightPane";
import { withDocumentPreviewPane } from "./useDocumentPreviewPane";
import {
	IconExternalLink,
	IconEye,
	IconFile,
	IconFileTypePdf,
} from "@tabler/icons-react";
import { makeAdminDetailsMenuItem } from "./Admin/AdminDetailsMenuItem";

const tagishMap = (id, available) => {
	return id
		? (() => {
				const matchingItem = available.filter((item) => item.id === id);

				if (matchingItem && matchingItem.length) {
					const val = matchingItem[0].name;
					return {
						label: val,
						value: val,
						id: id,
					};
				} else {
					return null;
				}
			})()
		: null;
};
const tagMap = (item) => ({ label: item.name, value: item.name, id: item.id });

const getLockText = (lockStatus, name) => {
	if (lockStatus === 0) {
		return `Currently locked by ${name || "???"}`;
	} else if (lockStatus === 1) {
		return `Currently in use by ${name || "???"}`;
	} else if (lockStatus === 2) {
		return `Currently checked out to ${name || "???"}`;
	} else if (lockStatus === 3) {
		return `Currently checked out to & in use by ${name || "???"}`;
	}
	return "";
};

class DocumentProperties extends Component {
	constructor(props, context) {
		super(props, context);
		this.state = {
			loading: true,
			documentName: "",
			documentType: {},
			documentStatus: {},
			documentTags: [],
			documentNotes: "",
			characterCount: "",
			ableToModify: true,
			documentNotInUse: true,
			basename: "",
			extension: "",
			excludeDocumentFromStamping: false,
			frontendRouteToContainingDirectory: "",
		};
		this.handleChange = this.handleChange.bind(this);
		this.toggleFavorite = this.toggleFavorite.bind(this);
		this.fileListChangeHandler = this.fileListChangeHandler.bind(this);
		this.openPreview = this.openPreview.bind(this);
		this.addVersionCommentHandler =
			this.addVersionCommentHandler.bind(this);
	}
	static getDerivedStateFromProps(props, state) {
		if (state.loading && !props.loading) {
			const { basename, extension } = splitFilename(
				props.documentDetails.filename
			);
			const { matterID, path, name } = props.documentDetails;

			const firmDocsPath = path.replace(
				/^\/(public|private)\//,
				"/$1/docs/"
			);
			const frontendRouteToContainingDirectory = matterID
				? `/matters/${matterID}${path}${name}/`
				: `/firmDocs${firmDocsPath}${name}/`;

			const newState = {
				loading: false,
				documentName: props.documentDetails.filename,
				documentType: tagishMap(
					props.documentDetails.documentTypeID,
					props.availableDocumentTypes
				),
				documentStatus: tagishMap(
					props.documentDetails.documentStatusID,
					props.availableDocumentStatuses
				),
				documentTags: props.documentDetails.tags
					? props.documentDetails.tags.map(tagMap)
					: [],
				documentNotes: props.documentDetails.notes
					? props.documentDetails.notes
					: "",
				characterCount: props.documentDetails.notes
					? props.documentDetails.notes.length + "/1000"
					: "",
				ableToModify:
					!props.documentDetails.lockedBySub ||
					props.documentDetails.lockedBySub === props.cognitoSub,
				documentNotInUse:
					props.documentDetails.lockStatus === 0 ||
					props.documentDetails.lockStatus === 2,
				basename: basename,
				extension: extension,
				excludeDocumentFromStamping: props.excludeDocumentFromStamping,
				frontendRouteToContainingDirectory,
			};
			return newState;
		} else if (props.loading && !state.loading) {
			return { loading: true };
		} else {
			return null;
		}
	}
	getUrlPrefix() {
		return (
			"/matters/" +
			this.props.match.params.id +
			"/" +
			this.props.match.params.root +
			"/"
		);
	}
	componentWillUnmount() {
		this.props.unmountDocumentProperties();
	}
	getDisabledStatusAndTextForStamping() {
		if (this.state.ableToModify) {
			if (
				this.props.stampDisabledType === "SINGLE_DOCUMENT" ||
				this.props.stampDisabledType === "" ||
				this.props.documentShouldBeStamped
			) {
				return {
					disabled: false,
					disabledHoverText: "",
				};
			} else {
				return {
					disabled: true,
					disabledHoverText: this.props.documentStampDisabledMessage,
				};
			}
		} else {
			return {
				disabled: true,
				disabledHoverText:
					"You do not have permission to modify this document",
			};
		}
	}
	handleChange(key, value) {
		var newState = { [key]: value };
		if (key === "documentNotes") {
			newState.characterCount = value.length + "/" + 1000;
			if (value.length > 1000) {
				newState.notesTooLong = true;
			} else {
				newState.notesTooLong = false;
			}
		} else if (key === "basename") {
			var totalFilenameLength =
				value.length + this.state.extension.length + 1;
			if (totalFilenameLength > 255) {
				newState.filenameTooLong = true;
			} else {
				newState.filenameTooLong = false;
			}
			//Check for case-insensitive duplicate in current folder
			newState.matchesFilename =
				this.props.documentFileNamesInSameDirectory.filter(
					(item) =>
						item.filename.toUpperCase() ===
						value.toUpperCase() +
							"." +
							this.state.extension.toUpperCase()
				).length;
			newState.matchesDeletedFilename =
				this.props.documentFileNamesInSameDirectory.filter(
					(item) =>
						item.filename.toUpperCase() ===
							value.toUpperCase() +
								"." +
								this.state.extension.toUpperCase() &&
						item.isDeleted
				).length;
			if (newState.matchesDeletedFilename) {
				console.log("deleted match");
			}
		}
		this.setState(newState);
	}
	submit() {
		if (
			this.state.ableToModify &&
			this.props.documentLifecycleStatusObject?.disabled
		) {
			this.props.renameDocumentFromDocumentProperties(
				this.props.documentDetails.documentID,
				this.state.extension === ""
					? this.state.basename
					: this.state.basename + "." + this.state.extension,
				this.state.frontendRouteToContainingDirectory
			);
		} else if (this.state.ableToModify) {
			this.props.updateDocumentProperties(
				this.props.match.params.fullpath,
				this.state,
				this.state.frontendRouteToContainingDirectory
			);
		} else {
			window.safeGoBack(this.state.frontendRouteToContainingDirectory);
		}
	}
	toggleFavorite() {
		this.props.setFavoriteDocumentProperties(
			this.props.documentDetails.documentID,
			!this.props.documentDetails.isFavorite
		);
	}
	componentDidMount() {
		this.props.getDocumentDetails(this.props.match.params.fullpath);
	}
	componentDidUpdate(prevProps) {
		if (
			prevProps.match.params.fullpath !== this.props.match.params.fullpath
		) {
			this.setState({ loading: true }, () => {
				this.props.getDocumentDetails(this.props.match.params.fullpath);
			});
		}
	}
	fileListChangeHandler(files, callback, isNewVersion = true) {
		if (this.state.documentNotInUse && this.state.ableToModify) {
			this.props.uploadNewVersionWithValidation(
				this.props.documentDetails,
				files,
				callback,
				null,
				isNewVersion
			);
		}
	}
	resolveVersionLabel(versionNumber) {
		const labels = this.props.documentManualVersioningData?.labels ?? [];
		if (!labels.length) {
			return versionNumber ? `v${versionNumber}` : undefined;
		}
		const label = labels.find((l) => l.saveNumber === versionNumber);
		if (label) return `v${label.major}`;
		return versionNumber ? `v${versionNumber}` : undefined;
	}
	openPreview(version) {
		const {
			versionNumber,
			versionID,
			versionLabel: passedLabel,
		} = version ??
		(this.props.documentList && this.props.documentList.length
			? this.props.documentList[0]
			: {});
		const versionLabel =
			passedLabel ?? this.resolveVersionLabel(versionNumber);
		this.props.documentPreviewPane.openItemPreview({
			type: "File",
			id: this.props.documentDetails.documentID,
			title: this.props.documentDetails.filename,
			rowData: {
				...this.props.documentDetails,
				versionNumber,
				versionID,
				versionLabel,
				documentLifecycleStatus:
					this.props.documentDetails.documentLifecycleStatus,
			},
		});
	}
	getUploadNewVersionDisabledInfo() {
		return {
			disabled:
				!this.state.documentNotInUse ||
				!this.state.ableToModify ||
				this.props.documentLifecycleStatusObject.disabled,
			disabledHoverText: this.props.documentLifecycleStatusObject.disabled
				? this.props.documentLifecycleStatusObject.disabledText
				: this.state.ableToModify && !this.state.documentNotInUse
					? "You have this document open in the launcher"
					: "This document is locked by another user",
		};
	}
	getOtherActionsMenuItems() {
		const ocrDisabledInfo = ocrIsDisabledForDocument({
			documentNotInUse: this.state.documentNotInUse,
			ableToModify: this.state.ableToModify,
			isOcrEnabled: this.props.isOcrEnabled,
			isTenantOverOcrLimit: this.props.isTenantOverOcrLimit,
			documentActionsAreDisabled:
				this.props.documentLifecycleStatusObject.disabled,
			documentActionsAreDisabledHoverText: this.props
				.documentLifecycleStatusObject.disabled
				? this.props.documentLifecycleStatusObject.disabledText
				: undefined,
			userRights: this.props.userRights,
		});

		let otherActions = [
			{
				onSelect: () =>
					this.props.openDocumentInLauncher(
						this.props.documentDetails.documentID,
						this.props.cognitoSub
					),

				disabled:
					!(
						this.props.launcherInstalled && this.state.ableToModify
					) || this.props.documentLifecycleStatusObject.disabled,
				disabledHoverText: this.props.documentLifecycleStatusObject
					.disabled
					? this.props.documentLifecycleStatusObject.disabledText
					: undefined,

				text: "Open Document",
				iconComponent: (
					<IconExternalLink
						size={16}
						style={{ verticalAlign: "middle" }}
					/>
				),
			},
			{
				onSelect: this.toggleFavorite,
				disabled: this.props.documentLifecycleStatusObject.disabled,
				disabledHoverText:
					this.props.documentLifecycleStatusObject.disabledText,
				text: (
					<div>
						<FavoriteStar
							isFavorite={this.props.documentDetails.isFavorite}
							style={{
								float: "left",
								marginRight: 12,
								top: 3,
								right: 2,
							}}
						/>
						<span>Favorite</span>
					</div>
				),
			},
			{
				onSelect: this.openPreview,
				text: "Preview",
				iconComponent: (
					<IconEye size={16} style={{ verticalAlign: "middle" }} />
				),
			},
		];
		if (convertToPDFAllowed(this.props.documentDetails.filename)) {
			otherActions.push({
				onSelect: () =>
					this.props.convertDocumentToPDF({
						sourceDocumentID: this.props.documentDetails.documentID,
						fileToConvert: this.props.documentDetails.filename,
						matterID: this.props.documentDetails.matterID,
						actionType: "createPDF",
					}),
				disabled: this.props.documentLifecycleStatusObject.disabled,
				disabledHoverText:
					this.props.documentLifecycleStatusObject.disabledText,
				text: "Convert to PDF",
				iconComponent: (
					<IconFileTypePdf
						size={16}
						style={{ verticalAlign: "middle" }}
					/>
				),
			});
		}
		if (ocrOnDemandAllowed(this.props.documentDetails.filename)) {
			otherActions.push({
				disabled: ocrDisabledInfo.disabled,
				disabledHoverText: ocrDisabledInfo.disabledHoverText,
				onSelect: () =>
					this.props.requestOCR(
						this.props.documentDetails.documentID,
						this.props.documentDetails.filename
					),
				text: "Request OCR",
				iconComponent: (
					<IconFileTypePdf
						size={16}
						style={{ verticalAlign: "middle" }}
					/>
				),
			});
		}
		return otherActions;
	}
	getActionButtonMenuItems() {
		let actionItems = [
			this.props.isLexworkplaceAdmin
				? makeAdminDetailsMenuItem(
						`/admin/tenant/${this.props.tenantID}/docs/${this.props.documentDetails.documentID}`
					)
				: null,
			{
				text: this.props.documentList.length
					? (this.props.documentList[0].comment ? "Edit" : "Add") +
						" v" +
						(this.props.documentList.length &&
							this.props.documentList[0].versionNumber) +
						" Version Comment"
					: "No Document Versions",
				disabled:
					!this.props.documentList.length ||
					!this.state.ableToModify ||
					this.props.documentLifecycleStatusObject.disabled,
				disabledHoverText:
					this.props.documentLifecycleStatusObject.disabledText,
				onSelect: this.addVersionCommentHandler,
			},
			{
				text: "Download a Copy",
				onSelect: () => {
					this.props.getSignedUrlForDocumentAndDownload(
						this.props.documentDetails.documentID
					);
				},
			},
			{
				text: "Reset Column Widths",
				onSelect: () => {
					this.props.resetTableColumnWidths(VERSIONS_MANUAL_TABLE_ID);
					this.props.resetTableColumnWidths(VERSIONS_AUTO_TABLE_ID);
				},
			},
		];
		if (
			!getDisabledStateFromLifecycleStatuses({
				matterLifecycleStatus:
					this.props.documentDetails.matterLifecycleStatus,
				folderLifecycleStatus:
					this.props.documentDetails.folderLifecycleStatus,
			}).disabled
		) {
			// we can allow moving to/from trash bin
			if (
				this.props.documentDetails.documentLifecycleStatus ===
				TRASH_LIFECYCLE_STATUS
			) {
				actionItems.push({
					text: "Restore Document from Trash Bin",
					onSelect: () =>
						this.props.setDocumentLifecycleStatus(
							this.props.documentDetails.documentID,
							NORMAL_LIFECYCLE_STATUS
						),
				});
			}
			if (
				this.props.documentDetails.documentLifecycleStatus ===
				NORMAL_LIFECYCLE_STATUS
			) {
				actionItems.push({
					text: "Move Document to Trash Bin",
					onSelect: () =>
						this.props.setDocumentLifecycleStatus(
							this.props.documentDetails.documentID,
							TRASH_LIFECYCLE_STATUS
						),
				});
			}
			// we can allow moving to/from archive, gated by the firm feature and CAN_ARCHIVE_MATTER_CONTENTS
			if (
				this.props.documentDetails.documentLifecycleStatus ===
				ARCHIVE_LIFECYCLE_STATUS
			) {
				const access = this.props.archiveMatterContentsAccess.unarchive;
				actionItems.push({
					text: "Restore Document from Archive",
					disabled: access.disabled,
					disabledHoverText: access.disabled
						? access.disabledText
						: "",
					onSelect: () =>
						this.props.setDocumentLifecycleStatus(
							this.props.documentDetails.documentID,
							NORMAL_LIFECYCLE_STATUS
						),
				});
			} else {
				const access = this.props.archiveMatterContentsAccess.archive;
				actionItems.push({
					text: "Move Document to Archive",
					disabled: access.disabled,
					disabledHoverText: access.disabled
						? access.disabledText
						: "",
					onSelect: () =>
						this.props.setDocumentLifecycleStatus(
							this.props.documentDetails.documentID,
							ARCHIVE_LIFECYCLE_STATUS
						),
				});
			}
		}
		return actionItems;
	}
	addVersionCommentHandler() {
		this.props.openGlobalDialog(
			{
				label: `Comment for Current Version (Save ${this.props.documentList[0].versionNumber})`,
				value: this.props.documentList[0].comment,
				canSubmit: true,
				submitButtonLabel: "Submit",
			},
			(state) => {
				this.props.updateVersionComment(
					this.props.documentDetails.documentID,
					this.props.documentList[0].versionNumber,
					this.props.documentList[0].versionID,
					state.value
				);
			},
			() => {
				return {
					canSubmit: true,
					erroMessage: "",
				};
			}
		);
	}
	render() {
		if (
			this.state.loading ||
			this.props.loading ||
			!this.props.documentDetails ||
			!this.props.documentList
		) {
			return (
				<div>
					<h1>
						Loading<span className="Loading">...</span>
					</h1>
				</div>
			);
		} else {
			const documentStampingDisabledStatusAndText =
				this.getDisabledStatusAndTextForStamping();

			const ocrDisabledInfo = ocrIsDisabledForDocument({
				documentNotInUse: this.state.documentNotInUse,
				ableToModify: this.state.ableToModify,
				isOcrEnabled: this.props.isOcrEnabled,
				isTenantOverOcrLimit: this.props.isTenantOverOcrLimit,
				documentActionsAreDisabled:
					this.props.documentLifecycleStatusObject.disabled,
				documentActionsAreDisabledHoverText:
					this.props.documentLifecycleStatusObject.disabledText,
				userRights: this.props.userRights,
			});

			return (
				<>
					{this.props.documentLifecycleStatusObject.IN_TRASH ||
					this.props.documentLifecycleStatusObject.IN_ARCHIVE
						? TrashOrArchiveBinMessageForDocumentOrNote({
								...this.props.documentLifecycleStatusObject,
								objectType: "Document",
							})
						: null}
					<TwoColumnWrapper
						headingText="Properties"
						iconComponent={
							<IconFile
								size={20}
								style={{ verticalAlign: "middle" }}
							/>
						}
						canSubmit={
							this.state.ableToModify &&
							this.state.documentName !== "" &&
							!this.state.filenameTooLong &&
							!this.state.notesTooLong &&
							!this.state.matchesFilename
						}
						submit={this.submit.bind(this)}
						submitButtonText="Update"
						defaultGoBackLocation={
							this.state.frontendRouteToContainingDirectory
						}
						cancelButtonText={
							this.state.ableToModify ? "Cancel" : "Back"
						}
						rightHeading={
							<>
								<div className="narrowScreenButtonGroup">
									<DropDownButtonWithRouter
										title="Actions"
										pullRight
										menuItems={this.getOtherActionsMenuItems()
											.concat(
												getCheckoutMenuItems({
													lockedByDisplay:
														this.props
															.documentDetails
															.lockedBy,
													lockedBySub:
														this.props
															.documentDetails
															.lockedBySub,
													lockStatus:
														this.props
															.documentDetails
															.lockStatus,
													documentID:
														this.props
															.documentDetails
															.documentID,
													basepath: null,
													userSub:
														this.props.cognitoSub,
													fn: this.props
														.setLockStatus,
													matterID: null,
													userCanOverrideDocumentLocks:
														this.props
															.canOverrideDocumentLocks,
													isDisabled:
														this.props
															.documentLifecycleStatusObject
															.disabled,
													disabledHoverText:
														this.props
															.documentLifecycleStatusObject
															.disabledText,
												})
											)
											.concat(
												this.getActionButtonMenuItems()
											)}
									/>
								</div>

								<div className="documentPropertiesButtonGroup">
									<ButtonWithIcon
										handler={() =>
											this.props.openDocumentInLauncher(
												this.props.documentDetails
													.documentID,
												this.props.cognitoSub
											)
										}
										disabled={
											!(
												this.props.launcherInstalled &&
												this.state.ableToModify
											) ||
											this.props
												.documentLifecycleStatusObject
												.disabled
										}
										disabledHoverText={
											this.props
												.documentLifecycleStatusObject
												.disabledText
										}
										buttonText="Open Document"
										iconComponent={
											<IconExternalLink
												size={16}
												style={{ verticalAlign: "sub" }}
											/>
										}
									/>
									<ButtonWithIcon
										handler={this.toggleFavorite}
										buttonText="Favorite"
										disabled={
											this.props
												.documentLifecycleStatusObject
												.disabled
										}
										disabledHoverText={
											this.props
												.documentLifecycleStatusObject
												.disabledText
										}
										iconComponent={
											<FavoriteStar
												isFavorite={
													this.props.documentDetails
														.isFavorite
												}
												style={{
													float: "left",
												}}
											/>
										}
									/>
									<ButtonWithIcon
										handler={this.openPreview}
										buttonText="Preview"
										iconComponent={
											<IconEye
												size={16}
												style={{
													verticalAlign: "middle",
												}}
											/>
										}
									/>
									{convertToPDFAllowed(
										this.props.documentDetails.filename
									) ? (
										<ButtonWithIcon
											handler={() =>
												this.props.convertDocumentToPDF(
													{
														sourceDocumentID:
															this.props
																.documentDetails
																.documentID,
														fileToConvert:
															this.props
																.documentDetails
																.filename,
														matterID:
															this.props
																.documentDetails
																.matterID,
														actionType: "createPDF",
													}
												)
											}
											disabled={
												this.props
													.documentLifecycleStatusObject
													.disabled
											}
											disabledHoverText={
												this.props
													.documentLifecycleStatusObject
													.disabledText
											}
											buttonText="Convert to PDF"
											iconComponent={
												<IconFileTypePdf
													size={16}
													style={{
														verticalAlign: "middle",
													}}
												/>
											}
										/>
									) : (
										""
									)}
									{ocrOnDemandAllowed(
										this.props.documentDetails.filename
									) ? (
										<ButtonWithIcon
											disabled={ocrDisabledInfo.disabled}
											disabledHoverText={
												ocrDisabledInfo.disabledHoverText
											}
											handler={() =>
												this.props.requestOCR(
													this.props.documentDetails
														.documentID,
													this.props.documentDetails
														.filename
												)
											}
											buttonText="Request OCR"
											iconComponent={
												<IconFileTypePdf
													size={16}
													style={{
														verticalAlign: "middle",
													}}
												/>
											}
										/>
									) : (
										""
									)}
									<div>
										<DropDownButtonWithRouter
											title="Actions"
											pullRight
											menuItems={getCheckoutMenuItems({
												lockedByDisplay:
													this.props.documentDetails
														.lockedBy,
												lockedBySub:
													this.props.documentDetails
														.lockedBySub,
												lockStatus:
													this.props.documentDetails
														.lockStatus,
												documentID:
													this.props.documentDetails
														.documentID,
												basepath: null,
												userSub: this.props.cognitoSub,
												fn: this.props.setLockStatus,
												matterID: null,
												userCanOverrideDocumentLocks:
													this.props
														.canOverrideDocumentLocks,
												isDisabled:
													this.props
														.documentLifecycleStatusObject
														.disabled,
												disabledHoverText:
													this.props
														.documentLifecycleStatusObject
														.disabledText,
											}).concat(
												this.getActionButtonMenuItems()
											)}
										/>
									</div>
								</div>
							</>
						}
						leftChildren={
							<div>
								<div>
									<div
										style={{
											marginBottom: 16,
										}}
									>
										<BreadcrumbsWithMatterName
											{...this.props.documentDetails}
											directoryHierarchy={
												this.props.directoryHierarchy
											}
										/>
									</div>

									<div>
										<div style={{ display: "flex" }}>
											<div style={{ flexGrow: 1 }}>
												<LabeledTextInput
													label="Document Name*"
													name="basename"
													placeholder="Document Name"
													defaultValue={
														this.state.basename
													}
													disabled={
														!this.state.ableToModify
													}
													onChange={this.handleChange}
												/>
											</div>
											{this.state.extension !== "" ? (
												<div
													style={{
														display: "relative",
														marginLeft: 8,
														paddingTop: 26,
													}}
												>
													.{this.state.extension}
												</div>
											) : null}
										</div>
										{this.state.filenameTooLong ? (
											<div className="inlineErrorIndicator">
												<div>
													Filename cannot be longer
													than 255 characters
												</div>
											</div>
										) : null}
										{this.state.matchesFilename ? (
											<div className="inlineErrorIndicator">
												<div>
													There is already a
													{this.state
														.matchesDeletedFilename
														? " deleted "
														: " "}
													file in the current
													directory with this name
												</div>
											</div>
										) : null}
									</div>
									<LabeledDropDownSelect
										label="Document Type"
										name="documentType"
										placeholder="Document Type"
										isCreatable={
											this.props.userRights
												.CRUD_DOCUMENT_TYPES
										}
										isClearable
										isAsync
										isDisabled={
											!this.state.ableToModify ||
											this.props
												.documentLifecycleStatusObject
												.disabled
										}
										options={
											this.props.availableDocumentTypes &&
											this.props.availableDocumentTypes
												.filter(
													(type) =>
														type.status === "active"
												)
												.map((type) => ({
													label: type.name,
													value: type.name,
													id: type.id,
												}))
										}
										defaultValue={tagishMap(
											this.props.documentDetails
												.documentTypeID,
											this.props.availableDocumentTypes
										)}
										handler={this.handleChange}
									/>
									<LabeledDropDownSelect
										label="Document Status"
										name="documentStatus"
										placeholder="Document Status"
										isCreatable={
											this.props.userRights
												.CRUD_DOCUMENT_STATUSES
										}
										isClearable
										isAsync
										isDisabled={
											!this.state.ableToModify ||
											this.props
												.documentLifecycleStatusObject
												.disabled
										}
										options={
											this.props
												.availableDocumentStatuses &&
											this.props.availableDocumentStatuses
												.filter(
													(type) =>
														type.status === "active"
												)
												.map((type) => ({
													label: type.name,
													value: type.name,
													id: type.id,
												}))
										}
										defaultValue={tagishMap(
											this.props.documentDetails
												.documentStatusID,
											this.props.availableDocumentStatuses
										)}
										handler={this.handleChange}
									/>
									<LabeledDropDownSelect
										label="Document Tag(s)"
										isMulti
										name="documentTags"
										isCreatable={
											this.props.userRights
												.CRUD_DOCUMENT_TAGS
										}
										isAsync
										isDisabled={
											!this.state.ableToModify ||
											this.props
												.documentLifecycleStatusObject
												.disabled
										}
										options={
											this.props.availableDocumentTags &&
											this.props.availableDocumentTags
												.filter(
													(type) =>
														type.status === "active"
												)
												.map((type) => ({
													label: type.name,
													value: type.name,
													id: type.id,
												}))
										}
										defaultValue={
											this.props.documentDetails.tags &&
											this.props.documentDetails.tags.map(
												tagMap
											)
										}
										handler={this.handleChange}
									/>
									<div style={{ position: "relative" }}>
										<LabeledTextArea
											label="Internal Notes"
											name="documentNotes"
											onChange={this.handleChange}
											disabled={
												!this.state.ableToModify ||
												this.props
													.documentLifecycleStatusObject
													.disabled
											}
											defaultValue={
												(this.props.documentDetails &&
													this.props.documentDetails
														.notes) ||
												""
											}
										/>
										{this.state.notesTooLong ? (
											<div className="inlineErrorIndicator">
												<div>
													Notes cannot be longer than
													1000 characters
												</div>
											</div>
										) : null}
										<div style={{ textAlign: "right" }}>
											{this.state.characterCount}
										</div>
									</div>
									<div className="LabeledInput">
										<div className="InputLabel">
											Document Stamping
										</div>
										{documentStampingDisabledStatusAndText.disabled ? (
											<div
												style={{
													border: "1px solid #ccc",
													borderRadius: 4,
													borderLeft: "8px solid",
													borderLeftColor:
														"var(--lxw-main-500)",
													padding: "0 8px",
													height: 38,
													fontSize: "12px",
													display: "flex",
													alignItems: "center",
												}}
											>
												{
													documentStampingDisabledStatusAndText.disabledHoverText
												}
											</div>
										) : (
											<SimpleSwitch
												label="Do Not Stamp This Document"
												labelVariant="div"
												variant="formInput"
												switchValue={
													this.state
														.excludeDocumentFromStamping
												}
												disabled={
													this.props
														.documentLifecycleStatusObject
														.disabled
												}
												handleSwitchValueChange={() => {
													this.setState({
														excludeDocumentFromStamping:
															!this.state
																.excludeDocumentFromStamping,
													});
												}}
											/>
										)}
									</div>
									<div>
										<div>
											<span
												className="MainFontBold"
												style={{ marginRight: 4 }}
											>
												Document ID:
											</span>
											<span>
												{
													this.props.match.params
														.fullpath
												}
											</span>
										</div>
										{this.props.documentDetails &&
										(this.props.documentDetails.lockStatus >
											0 ||
											this.props.documentDetails
												.lockedBy) ? (
											<div>
												{getLockText(
													this.props.documentDetails
														.lockStatus,
													this.props.documentDetails
														.lockedBy
												)}
											</div>
										) : null}
										<div>
											Created on{" "}
											{longDateFormat(
												this.props.documentDetails
													.createdAt
											) +
												" by " +
												this.props.documentDetails
													.createdBy}
										</div>
										<div>
											Last Modified on{" "}
											{this.props.documentList &&
												this.props.documentList
													.length &&
												longDateFormat(
													this.props.documentList[0]
														.createdAt
												) + " "}{" "}
											by{" "}
											{this.props.documentList &&
												this.props.documentList
													.length &&
												this.props.documentList[0]
													.createdBy}
										</div>
										<div>
											Current Version:{" "}
											{this.props.currentMajorVersion !==
											null
												? `v${this.props.currentMajorVersion}${this.props.currentMinorVersion !== null ? `.${this.props.currentMinorVersion}` : ""}`
												: null}
										</div>
									</div>
								</div>
							</div>
						}
						rightChildren={
							<div style={{ position: "relative" }}>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										marginBottom: 16,
									}}
								>
									<div className="MainFontBold">
										Document Versions
									</div>
									<UploadVersionSplitButton
										disabled={
											!this.state.documentNotInUse ||
											!this.state.ableToModify ||
											this.props
												.documentLifecycleStatusObject
												.disabled
										}
										disabledHoverText={
											this.props
												.documentLifecycleStatusObject
												.disabled
												? this.props
														.documentLifecycleStatusObject
														.disabledText
												: this.state.ableToModify &&
													  !this.state
															.documentNotInUse
													? "You have this document open in the launcher"
													: "This document is locked by another user"
										}
										fileListChangeHandler={
											this.fileListChangeHandler
										}
										uploadAccept={
											this.state.extension
												? "." + this.state.extension
												: ""
										}
										firmHasManualVersioningEnabled={
											this.props
												.firmHasManualVersioningEnabled
										}
									/>
								</div>
								<DocumentVersions
									firmHasManualVersioningEnabled={
										this.props
											.firmHasManualVersioningEnabled
									}
									documentManualVersioningData={
										this.props.documentManualVersioningData
									}
									documentLifecycleStatusObject={
										this.props.documentLifecycleStatusObject
									}
									majorVersionsData={
										this.props.majorVersionsData
									}
									documentList={this.props.documentList}
									documentID={
										this.props.documentDetails.documentID
									}
									documentDetails={this.props.documentDetails}
									userRights={this.props.userRights}
									ableToModify={this.state.ableToModify}
									documentNotInUse={
										this.state.documentNotInUse
									}
									cognitoSub={this.props.cognitoSub}
									deleteMajorVersion={
										this.props.deleteMajorVersion
									}
									deleteVersion={this.props.deleteVersion}
									openGlobalDialog={
										this.props.openGlobalDialog
									}
									updateVersionComment={
										this.props.updateVersionComment
									}
									updateMajorVersionComment={
										this.props.updateMajorVersionComment
									}
									getSignedUrlForDocumentAndDownload={
										this.props
											.getSignedUrlForDocumentAndDownload
									}
									openDocumentInLauncher={
										this.props.openDocumentInLauncher
									}
									addDocumentToCompare={
										this.props.addDocumentToCompare
									}
									firstDocIdInDocumentCompare={
										this.props.firstDocIdInDocumentCompare
									}
									firstVersionIdInDocumentCompare={
										this.props
											.firstVersionIdInDocumentCompare
									}
									docCompareType={this.props.docCompareType}
									isValidLiteraFileType={
										this.props.isValidLiteraFileType
									}
									isValidInlineFileType={
										this.props.isValidInlineFileType
									}
									isLiteraCompareVisible={
										this.props.isLiteraCompareVisible
									}
									isLiteraCompareEnabled={
										this.props.isLiteraCompareEnabled
									}
									isInlineCompareEnabled={
										this.props.isInlineCompareEnabled
									}
									launcherInstalled={
										this.props.launcherInstalled
									}
									onPreview={this.openPreview}
									promoteDocumentToNextMajorVersion={
										this.props
											.promoteDocumentToNextMajorVersion
									}
								/>
							</div>
						}
					/>
					<DocumentSlidingPane
						openPane={this.props.documentPreviewPane.previewOpen}
						selectedRowData={
							this.props.documentPreviewPane.previewSelectedRow ??
							{}
						}
						closeRightPane={
							this.props.documentPreviewPane.closePreview
						}
						showVersion
					/>
				</>
			);
		}
	}
}
DocumentProperties.propTypes = {
	loading: PropTypes.bool.isRequired,
};
const mapStateToProps = (state) => {
	const documentLifecycleStatusObject = getDisabledStateFromLifecycleStatuses(
		{
			matterLifecycleStatus:
				state.document.documentDetails.matterLifecycleStatus,
			folderLifecycleStatus:
				state.document.documentDetails.folderLifecycleStatus,
			documentLifecycleStatus:
				state.document.documentDetails.documentLifecycleStatus,
		}
	);
	const stateProps = {
		matterDetails: state.matter.matterDetails,
		documentList: state.document.documentVersions,
		documentDetails: state.document.documentDetails,
		cognitoSub: state.user.cognitoUser.attributes.sub,
		availableDocumentTags: state.document.availableDocumentTags,
		availableDocumentTypes: state.document.availableDocumentTypes,
		availableDocumentStatuses: state.document.availableDocumentStatuses,
		documentFileNamesInSameDirectory:
			state.document.documentFileNamesInSameDirectory,
		prefix: state.document.prefix,
		loading: state.document.documentPropertiesLoading,
		launcherInstalled: state.global.launcherInstalled,
		canOverrideDocumentLocks:
			state.user.userRights.CAN_OVERRIDE_DOCUMENT_LOCK === true,
		isOcrEnabled: !state.tenant.tenantFeaturesLoading
			? state.tenant.tenantFeatures.automaticOCR === 1
			: true,
		isTenantOverOcrLimit: !state.tenant.tenantFeaturesLoading
			? state.tenant.tenantUsage.OCR_TRANSACTIONS_THIS_MONTH >=
				state.tenant.tenantLimits.OCR_PER_MONTH_NET
			: false,
		isLiteraCompareEnabled: !state.tenant.tenantFeaturesLoading
			? state.tenant.tenantFeatures.literaDocumentCompareEnabled === 1
			: true,
		isInlineCompareEnabled: !state.tenant.tenantFeaturesLoading
			? state.tenant.tenantFeatures.inlineDocumentCompareEnabled === 1
			: true,
		isLiteraCompareVisible: !state.tenant.tenantFeaturesLoading
			? state.tenant.tenantFeatures.literaDocumentCompareVisible === 1
			: true,
		firstDocIdInDocumentCompare: state.documentCompare.doc1ID,
		firstVersionIdInDocumentCompare: state.documentCompare.doc1VersionID,
		docCompareType: state.documentCompare.type,
		isValidLiteraFileType: isFileTypeCompatibleWithLitera(
			state.document.documentDetails.filename
		),
		isValidInlineFileType: isFileTypeCompatibleWithInline(
			state.document.documentDetails.filename
		),
		excludeDocumentFromStamping:
			!!state.document.documentStampingInformation
				.documentIsExcludedFromStamping,
		documentStampDisabledMessage:
			state.document.documentStampingInformation.message,
		stampDisabledType:
			state.document.documentStampingInformation.stampDisabledType,
		documentShouldBeStamped:
			state.document.documentStampingInformation.shouldStamp,
		directoryHierarchy: state.document.directoryHierarchy,
		documentLifecycleStatusObject,
		userRights: state.user.userRights,
		archiveMatterContentsAccess: selectArchiveMatterContentsAccess(state),
		documentManualVersioningData:
			state.document.documentManualVersioningData,
		firmHasManualVersioningEnabled:
			!!state.tenant.tenantFeatures.manualVersioning,
		currentMajorVersion: state.document.currentMajorVersion,
		currentMinorVersion: state.document.currentMinorVersion,
		majorVersionsData: state.document.majorVersionsData,
		isLexworkplaceAdmin:
			state.user.cognitoUser.cognitoGroups?.includes(
				"LexWorkplace_Admin"
			) ?? false,
		tenantID: state.user.cognitoUser.attributes?.["custom:tenantID"],
	};
	return stateProps;
};
export default connect(mapStateToProps, {
	getDocumentDetails,
	unmountDocumentProperties,
	updateDocumentProperties,
	setFavoriteDocumentProperties,
	uploadNewVersionWithValidation,
	openGlobalDialog,
	deleteVersion,
	deleteMajorVersion,
	deleteDocuments,
	restoreDocuments,
	setLockStatus,
	getSignedUrlForDocumentAndDownload,
	updateVersionComment,
	updateMajorVersionComment,
	openDocumentInLauncher,
	convertDocumentToPDF,
	requestOCR,
	resetTableColumnWidths,
	addDocumentToCompare,
	setDocumentLifecycleStatus,
	renameDocumentFromDocumentProperties,
	promoteDocumentToNextMajorVersion,
})(withDocumentPreviewPane(DocumentProperties));
